'use strict';

import {gsap} from 'gsap';

const $ = jQuery.noConflict();
$(function() {

	const heroCardsSlider = {

		// Config
		DURATION: 0.6,
		EASE: 'power3.out',
		MAX_VISIBLE: 3,
		OFFSET_X_PCT: 25,
		SCALE_STEP: 0.12,
		ROTATION_Y: 6,
		EXIT_PROPS: {x: -300, opacity: 0, rotationY: 30, scale: 0.85, transformPerspective: 1200},

		init: function() {
			this.cacheDOM();
			if (!this.$stack.length) return;

			const total = this.$cards.length;

			if (total <= 1) {
				this.$controls.addClass('is-disabled');
				this.$nav.hide();
			}

			this.visible = Math.min(this.MAX_VISIBLE, total);
			this.current = 0;
			this.total = total;
			this.animating = false;

			this.setupStack();
			this.updateProgress();
			this.$stack.addClass('is-ready');
			this.bindEvents();
		},

		cacheDOM: function() {
			this.$stack = $('.js-hcs-stack');
			this.$cards = this.$stack.find('.js-hcs-card');
			this.$prev = $('.js-hcs-prev');
			this.$next = $('.js-hcs-next');
			this.$nav = $('.js-hcs-nav');
			this.$controls = $('.js-hcs-controls');
			this.$progressBar = $('.js-hcs-progress');
		},

		/**
		 * Calculates the X offset in pixels based on card width percentage.
		 * This keeps the spacing proportional across all screen sizes.
		 */
		getOffsetX: function() {
			return (this.OFFSET_X_PCT / 100) * this.$cards.eq(0).outerWidth();
		},

		/**
		 * Returns GSAP properties for a card at a given stack position.
		 * Position 0 = front card, 1 = first behind, etc.
		 * Positions >= visible return hidden/off-stack props.
		 */
		getCardProps: function(position) {
			const isVisible = position < this.visible;
			const idx = isVisible ? position : this.visible;

			return {
				x: idx * this.getOffsetX(),
				scale: 1 - (idx * this.SCALE_STEP),
				rotationY: -(idx * this.ROTATION_Y),
				transformPerspective: 1200,
				opacity: isVisible ? 1 : 0,
				zIndex: isVisible ? this.visible - position : 0,
				visibility: isVisible ? 'visible' : 'hidden',
				pointerEvents: isVisible ? 'auto' : 'none',
			};
		},

		/**
		 * Animates all cards (except skipIndex) to their correct
		 * stack positions relative to newFrontIndex.
		 */
		layoutCards: function(newFrontIndex, skipIndex) {
			this.$cards.each((i, el) => {
				if (i === skipIndex) return;

				const position = (i - newFrontIndex + this.total) % this.total;
				const props = this.getCardProps(position);

				if (position < this.visible) {
					gsap.set(el, {visibility: 'visible'});
					gsap.to(el, {
						...props,
						duration: this.DURATION,
						ease: this.EASE,
						onComplete: () => {
							$(el).toggleClass('is-active', position === 0);
						},
					});
				} else {
					gsap.to(el, {
						opacity: 0,
						duration: this.DURATION * 0.5,
						ease: this.EASE,
						onComplete: () => {
							gsap.set(el, props);
							$(el).removeClass('is-active');
						},
					});
				}
			});
		},

		setupStack: function() {
			this.$cards.each((i, el) => {
				const position = (i - this.current + this.total) % this.total;
				gsap.set(el, this.getCardProps(position));
				$(el).toggleClass('is-active', position === 0);
			});
		},

		updateProgress: function() {
			if (!this.$progressBar.length) return;

			if (this.total <= 1) {
				this.$progressBar.css('transform', 'scaleX(1)');
				return;
			}

			const calc = this.current / (this.total - 1);
			this.$progressBar.css('transform', `scaleX(${calc})`);
		},

		bindEvents: function() {
			this.$next.on('click', () => this.navigate(1));
			this.$prev.on('click', () => this.navigate(-1));

			// Click on back card navigates slider to it, front card follows link
			this.$cards.on('click', (e) => {
				const $card = $(e.currentTarget);
				if ($card.hasClass('is-active')) return;

				e.preventDefault();
				this.navigate(null, this.$cards.index($card));
			});

			$(document).on('keydown', (e) => {
				if (e.key === 'ArrowRight') this.navigate(1);
				if (e.key === 'ArrowLeft') this.navigate(-1);
			});

			let resizeTimer;
			$(window).on('resize', () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => this.setupStack(), 150);
			});
		},

		/**
		 * Unified navigation method.
		 * @param {number|null} dir - Direction: 1 (next), -1 (prev), null (direct jump)
		 * @param {number} [targetIndex] - Direct target index (when dir is null)
		 */
		navigate: function(dir, targetIndex) {
			if (this.animating) return;

			const target = targetIndex ?? (this.current + dir + this.total) % this.total;
			if (target === this.current) return;

			this.animating = true;
			const isPrev = dir === -1;
			const canHideCards = this.total > this.visible;

			if (canHideCards) {
				if (isPrev) {
					// Animate incoming card from the left
					const incoming = this.$cards.eq(target)[0];

					gsap.set(incoming, {
						...this.EXIT_PROPS,
						visibility: 'visible',
						zIndex: this.visible,
					});

					gsap.to(incoming, {
						...this.getCardProps(0),
						duration: this.DURATION,
						ease: this.EASE,
						onComplete: () => $(incoming).addClass('is-active'),
					});

					this.layoutCards(target, target);
				} else {
					// Animate current front card out to the left
					const outgoing = this.$cards.eq(this.current)[0];

					gsap.to(outgoing, {
						...this.EXIT_PROPS,
						duration: this.DURATION,
						ease: this.EASE,
						onComplete: () => {
							gsap.set(outgoing, {visibility: 'hidden', pointerEvents: 'none'});
							$(outgoing).removeClass('is-active');
						},
					});

					this.layoutCards(target, this.current);
				}
			} else {
				this.layoutCards(target);
			}

			this.current = target;
			this.updateProgress();

			gsap.delayedCall(this.DURATION, () => {
				this.animating = false;
			});
		},

	};

	heroCardsSlider.init();
});
